import { useMutation } from "@apollo/client";
import { parseError } from "apollo";
import Button from "components/base/Button";
import { Text } from "components/base/Form";
import Page from "components/base/Page";
import { Alert } from "components/base/Toast";
import ImagesView from "components/shared/ImagesView";
import { useEffect, useRef, useState } from "react";
import BasicInfoView from "./BasicInfoView";
import CostView from "./CostView";
import { COMPLETE_PROJECT, SAVE_PROJECT } from "./graphql";
import GTMProjectsView from "./GTMProjectsView";
import NotesView from "./NotesView";
import OwnerView from "./OwnerView";
import ProjectContext from "./ProjectContext";
import TasksView from "./TasksView";

const defaultValues = {
  productType: "NEW_PRODUCT",
  targetPrice: 0,
  costCompany: "AB",
  bomQty: 1,
  components: [],
  orderQty: 3000,
  images: [],
  version: 1,
  packagings: [],
};

const ProjectForm = ({ originalProject, originalProjectData }) => {
  const [project, setProject] = useState({
    goal: originalProject.goal,
    priority: originalProject.priority,
    startDate: originalProject.startDate,
    sampleDate: originalProject.sampleDate,
    targetDate: originalProject.targetDate,
  });
  const [data, setData] = useState({
    ...defaultValues,
    ...originalProjectData,
  });
  const [images, setImages] = useState([]);

  const [saveProject, saveProjectRes] = useMutation(SAVE_PROJECT, {
    onError(error) {
      Alert("error", parseError(error));
    },
  });
  const [completeProject, completeProjectRes] = useMutation(COMPLETE_PROJECT, {
    variables: {
      id: originalProject.id,
    },
    onCompleted(res) {
      Alert("success", "Project completed successfully");
    },
    onError(error) {
      Alert("error", parseError(error));
    },
  });
  const [autoSaved, setAutoSaved] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (autoSaved) {
      const timer = setTimeout(() => {
        setAutoSaved(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [autoSaved]);

  function onChange({
    project: projectValues,
    data: dataValues,
    images: imagesValues,
  }) {
    const nextProject = projectValues
      ? { ...project, ...projectValues }
      : project;
    const nextData = dataValues ? { ...data, ...dataValues } : data;
    const nextImages = imagesValues ? imagesValues : images;

    if (projectValues) setProject(nextProject);
    if (dataValues) setData(nextData);
    if (imagesValues) setImages(nextImages);

    // debounce saves: clear any pending timer and schedule a new save
    if (timer.current) clearTimeout(timer.current);

    timer.current = setTimeout(() => {
      saveHandler(nextProject, nextData, nextImages, true);
    }, 1000);
  }

  function saveHandler(project, data, images, isAutoSave = false) {
    console.log("saveHandler", project, data, images);
    saveProject({
      variables: {
        id: originalProject.id,
        ...project,
        data: JSON.stringify(data, " ", 2),
        images: images.join(","),
      },
      onCompleted() {
        if (isAutoSave) {
          setAutoSaved(true);
        } else {
          Alert("success", "Project saved successfully!");
        }
      },
    });
  }

  return (
    <ProjectContext.Provider
      value={{ originalProject, saveProject, project, data, images, onChange }}
    >
      <Page
        title={
          <div className="flex items-baseline space-x-2">
            <div>Team Red: {originalProject.name}</div>
          </div>
        }
        backTo="./.."
        rightButtons={
          <div className="flex items-center space-x-4">
            {autoSaved && (
              <div className="text-green-600 font-bold">Auto Saved</div>
            )}

            {!originalProject.completedAt && (
              <Button
                onClick={() => saveHandler(project, data, images, false)}
                loading={saveProjectRes.loading}
              >
                Save
              </Button>
            )}

            <a
              href={`${process.env.REACT_APP_SERVER_ADMIN_URL + "projects/project/" + originalProject.id}`}
              target="_blank"
            >
              Admin
            </a>
          </div>
        }
      >
        <div className="grid grid-cols-12 gap-6 p-6">
          <div className="col-span-8 flex flex-col space-y-6">
            <BasicInfoView />
            <CostView data={data} onChange={onChange} />
            <NotesView />
          </div>
          <div className="col-span-4 flex flex-col space-y-6">
            <div className="card space-y-4">
              <div>
                <h5>Function Wishlist</h5>
                <Text
                  className="mt-2 -mx-1"
                  value={data.wishlist}
                  onChange={(e) =>
                    onChange({ data: { wishlist: e.target.value } })
                  }
                />
              </div>
              <div>
                <h5>Challenge</h5>
                <Text
                  className="mt-2 -mx-1"
                  value={data.challenge}
                  onChange={(e) =>
                    onChange({ data: { challenge: e.target.value } })
                  }
                />
              </div>
            </div>

            <TasksView projectId={originalProject.id} />

            <OwnerView />

            <GTMProjectsView />

            <ImagesView
              images={originalProject.imageUrls}
              onChange={(newImages) => onChange({ images: newImages })}
            />
          </div>
        </div>
      </Page>
    </ProjectContext.Provider>
  );
};

export default ProjectForm;
